import { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Grid } from "@mui/material";
import { Box } from "@mui/material";
import { DEFAULT_COUNTRY } from "./constants";
import { AddressInput } from "@/graphql/generated/graphql";
import { REGIONS } from "@/utils/UK";

interface SiteAddressFormProps {
  onAddressChange: (key: string, value: string) => void;
  address: AddressInput;
}

type AddressField = "street" | "city" | "state" | "zip";

const SiteAddressForm: React.FC<SiteAddressFormProps> = ({ onAddressChange, address }) => {
  // Show "required" errors only after the user has left the field
  const [touched, setTouched] = useState<Record<AddressField, boolean>>({
    street: false,
    city: false,
    state: false,
    zip: false,
  });
  const markTouched = (field: AddressField) => setTouched((prev) => ({ ...prev, [field]: true }));
  const showError = (field: AddressField) => touched[field] && !address?.[field]?.trim();

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      id="address-section"
      size={12}>
      <Grid size={12}>
        <TextField
          required
          name="street-address"
          label="Street Address"
          fullWidth
          value={address?.street ?? ""}
          error={showError("street")}
          helperText={showError("street") ? "Street is required" : ""}
          onChange={(e) => onAddressChange("street", e.target.value)}
          onBlur={() => markTouched("street")}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          fullWidth
          label="Country"
          disabled
          value={DEFAULT_COUNTRY}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <Autocomplete
          options={REGIONS}
          autoHighlight
          value={address?.state || null}
          onChange={(_, newValue: string | null) => {
            onAddressChange("state", newValue ?? "");
          }}
          onBlur={() => markTouched("state")}
          getOptionLabel={(option) => option}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              {option}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="State / Province"
              error={showError("state")}
              helperText={showError("state") ? "State / Province is required" : ""}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  autoComplete: "off",
                }
              }}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          value={address?.city ?? ""}
          fullWidth
          label="City"
          required
          onChange={(e) => onAddressChange("city", e.target.value)}
          onBlur={() => markTouched("city")}
          error={showError("city")}
          helperText={showError("city") ? "City is required" : ""}
          slotProps={{
            htmlInput: {
              autoComplete: "off",
            }
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TextField
          required
          name="zip"
          value={address?.zip ?? ""}
          label="Zip / Postal code"
          fullWidth
          autoComplete="postal-code"
          onChange={(e) => onAddressChange("zip", e.target.value)}
          onBlur={() => markTouched("zip")}
          error={showError("zip")}
          helperText={showError("zip") ? "Zip / Postal code is required" : ""}
        />
      </Grid>
    </Grid>
  );
};

export default SiteAddressForm;
